import { useState, useEffect } from 'react'
import { LayoutTemplate, Plus, Search, Tag, ChevronRight, Star } from 'lucide-react'
import { SYSTEM_TEMPLATES } from '../data/templates/system-templates'

interface Template {
  template_id: string
  scope: string
  name: string
  description: string
  industry: string | null
  decision_type: string
  default_question: string
  recommended_passes: string[]
  usage_count?: number
}

const DECISION_TYPE_LABELS: Record<string, string> = {
  concept_test: 'Concept Test',
  message_test: 'Message Test',
  pricing_reaction: 'Pricing Reaction',
  claims_renewal_copy: 'Claims / Renewal',
  agent_script: 'Agent Script',
  churn_upgrade: 'Churn / Upgrade',
  onboarding: 'Onboarding',
  feature_launch_comms: 'Feature Launch',
}

const INDUSTRY_LABELS: Record<string, string> = {
  insurance: 'Insurance',
  saas: 'SaaS',
  fmcg: 'FMCG',
  financial_services: 'Financial Services',
}

const DECISION_TYPES = Object.keys(DECISION_TYPE_LABELS)
const INDUSTRIES = Object.keys(INDUSTRY_LABELS)

function TemplateBadge({ label, variant = 'default' }: { label: string; variant?: 'default' | 'industry' | 'system' }) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
  const variants = {
    default: 'bg-echo-indigo/10 text-echo-indigo',
    industry: 'bg-amber-50 text-amber-700 border border-amber-200',
    system: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  }
  return <span className={`${base} ${variants[variant]}`}>{label}</span>
}

function TemplateCard({
  template,
  onUse,
}: {
  template: Template
  onUse: (id: string) => void
}) {
  const isSystem = template.scope === 'system'
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3 hover:border-echo-indigo/40 hover:shadow-sm transition-all">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <LayoutTemplate className="w-4 h-4 text-echo-indigo shrink-0 mt-0.5" />
          <h3 className="font-semibold text-gray-900 text-sm leading-snug">{template.name}</h3>
        </div>
        {isSystem && <TemplateBadge label="System" variant="system" />}
      </div>

      <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{template.description}</p>

      <div className="flex flex-wrap gap-1.5">
        {DECISION_TYPE_LABELS[template.decision_type] && (
          <TemplateBadge label={DECISION_TYPE_LABELS[template.decision_type]} />
        )}
        {template.industry && INDUSTRY_LABELS[template.industry] && (
          <TemplateBadge label={INDUSTRY_LABELS[template.industry]} variant="industry" />
        )}
      </div>

      <p className="text-xs text-gray-400 italic leading-relaxed line-clamp-2">
        "{template.default_question}"
      </p>

      <div className="flex items-center justify-between mt-auto pt-1">
        <span className="text-xs text-gray-400">{template.recommended_passes.length} passes</span>
        <button
          onClick={() => onUse(template.template_id)}
          className="flex items-center gap-1 text-xs font-medium text-echo-indigo hover:text-echo-indigo/80 transition-colors"
        >
          Use template <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}

export function TemplatesPage() {
  const [search, setSearch] = useState('')
  const [filterDecision, setFilterDecision] = useState<string | null>(null)
  const [filterIndustry, setFilterIndustry] = useState<string | null>(null)
  const [tenantTemplates, setTenantTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [usedTemplate, setUsedTemplate] = useState<string | null>(null)

  useEffect(() => {
    const fetchTenantTemplates = async () => {
      try {
        const res = await fetch('/api/v1/templates')
        if (res.ok) {
          const data = await res.json() as { tenant_templates: Template[] }
          setTenantTemplates(data.tenant_templates ?? [])
        }
      } catch {
        // fall through — demo mode
      } finally {
        setLoading(false)
      }
    }
    fetchTenantTemplates()
  }, [])

  const handleUse = async (templateId: string) => {
    setUsedTemplate(templateId)
    try {
      await fetch(`/api/v1/templates/${templateId}/use`, { method: 'POST' })
    } catch {
      // ignore
    }
    setTimeout(() => setUsedTemplate(null), 2000)
  }

  const allTemplates: Template[] = [
    ...(SYSTEM_TEMPLATES as unknown as Template[]),
    ...tenantTemplates,
  ]

  const filtered = allTemplates.filter(t => {
    const matchSearch =
      !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase()) ||
      t.default_question.toLowerCase().includes(search.toLowerCase())
    const matchDecision = !filterDecision || t.decision_type === filterDecision
    const matchIndustry = !filterIndustry || t.industry === filterIndustry
    return matchSearch && matchDecision && matchIndustry
  })

  const system = filtered.filter(t => t.scope === 'system')
  const tenant = filtered.filter(t => t.scope !== 'system')

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Pre-Read Templates</h1>
          <p className="text-sm text-gray-500 mt-1">
            Start a pre-read with pre-filled questions, segments, and passes.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-echo-indigo text-white text-sm font-medium rounded-lg hover:bg-echo-indigo/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48 max-w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
          />
        </div>

        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-gray-400" />
          <select
            value={filterDecision ?? ''}
            onChange={e => setFilterDecision(e.target.value || null)}
            className="text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
          >
            <option value="">All types</option>
            {DECISION_TYPES.map(dt => (
              <option key={dt} value={dt}>{DECISION_TYPE_LABELS[dt]}</option>
            ))}
          </select>
        </div>

        <select
          value={filterIndustry ?? ''}
          onChange={e => setFilterIndustry(e.target.value || null)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
        >
          <option value="">All industries</option>
          {INDUSTRIES.map(ind => (
            <option key={ind} value={ind}>{INDUSTRY_LABELS[ind]}</option>
          ))}
        </select>

        {(filterDecision || filterIndustry || search) && (
          <button
            onClick={() => { setFilterDecision(null); setFilterIndustry(null); setSearch('') }}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Clear filters
          </button>
        )}
      </div>

      {usedTemplate && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
          <Star className="w-4 h-4" />
          Template applied — your pre-read form has been pre-filled.
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-52 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {system.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                System Templates
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {system.map(t => (
                  <TemplateCard key={t.template_id} template={t} onUse={handleUse} />
                ))}
              </div>
            </section>
          )}

          {tenant.length > 0 && (
            <section>
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Your Workspace Templates
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {tenant.map(t => (
                  <TemplateCard key={t.template_id} template={t} onUse={handleUse} />
                ))}
              </div>
            </section>
          )}

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <LayoutTemplate className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No templates match your filters.</p>
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <CreateTemplateModal onClose={() => setShowCreateModal(false)} onCreated={() => {
          setShowCreateModal(false)
          setLoading(true)
          fetch('/api/v1/templates')
            .then(r => r.json())
            .then((d: unknown) => {
              const data = d as { tenant_templates: Template[] }
              setTenantTemplates(data.tenant_templates ?? [])
            })
            .catch(() => {})
            .finally(() => setLoading(false))
        }} />
      )}
    </div>
  )
}

function CreateTemplateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [decisionType, setDecisionType] = useState('')
  const [industry, setIndustry] = useState('')
  const [question, setQuestion] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (!name.trim() || !decisionType) {
      setError('Name and type are required.')
      return
    }
    setSaving(true)
    try {
      const res = await fetch('/api/v1/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, description, decision_type: decisionType,
          industry: industry || null, default_question: question,
        }),
      })
      if (!res.ok) throw new Error('Failed to save')
      onCreated()
    } catch {
      setError('Failed to save template. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">New Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Template name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Q3 Renewal Copy Test"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description…"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Decision type *</label>
              <select
                value={decisionType}
                onChange={e => setDecisionType(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
              >
                <option value="">Select…</option>
                {DECISION_TYPES.map(dt => (
                  <option key={dt} value={dt}>{DECISION_TYPE_LABELS[dt]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Industry (optional)</label>
              <select
                value={industry}
                onChange={e => setIndustry(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
              >
                <option value="">None</option>
                {INDUSTRIES.map(ind => (
                  <option key={ind} value={ind}>{INDUSTRY_LABELS[ind]}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Default question</label>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              rows={2}
              placeholder="How would [segment] respond to…"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30 resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 justify-end pt-2">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-echo-indigo text-white text-sm font-medium rounded-lg hover:bg-echo-indigo/90 disabled:bg-button-disabled-bg disabled:text-button-disabled-text disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  )
}
